import React from 'react';

/**
 * Notched-corner HUD panel with a thin glowing border.
 * Usage matches the design handoff: <Panel title sub action notch>{children}</Panel>
 */
export function Panel({ title, sub, action, children, className = '', notch = true, accent, style }) {
  const panelStyle = accent ? { borderTopColor: accent } : undefined;
  return (
    <div className={`panel-wrap ${className}`} style={style}>
      <div className={`panel ${notch ? '' : 'square'}`} style={panelStyle}>
        <div className="panel-inner">
          {(title || action) && (
            <div className="panel-head">
              {title && (
                <h3>
                  {title}
                  {sub && <span className="sub" style={{ marginLeft: 8 }}>{sub}</span>}
                </h3>
              )}
              {action}
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

export function Chip({ children, tone = '', style, title }) {
  return <span className={`chip ${tone}`} style={style} title={title}>{children}</span>;
}

export function Btn({ children, tone = '', onClick, icon, type = '', disabled, title, style }) {
  return (
    <button className={`btn ${tone} ${type}`} onClick={onClick} disabled={disabled} title={title} style={style}>
      {icon}
      {children}
    </button>
  );
}

export function Dot({ tone = '' }) {
  return <span className={`dot ${tone}`} />;
}

export function Toggle({ on, onChange, disabled }) {
  return (
    <div
      className={`toggle ${on ? 'on' : ''}`}
      onClick={() => !disabled && onChange?.(!on)}
      role="switch"
      aria-checked={on}
      style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
    />
  );
}

export function Sparkline({ data = [], color }) {
  return (
    <div className="spark">
      {data.map((v, i) => (
        <i
          key={i}
          style={{ height: `${Math.max(8, v * 100)}%`, background: color || undefined, opacity: 0.45 + v * 0.5 }}
        />
      ))}
    </div>
  );
}

/**
 * Circular portrait. Renders a real EVE character portrait when `src` is given,
 * otherwise falls back to an initial-letter placeholder over the HUD gradient.
 */
export function Portrait({ name, size = 36, src, current, className = '' }) {
  const initial = (name || '?').charAt(0).toUpperCase();
  return (
    <div
      className={`portrait ${src ? 'has-img' : ''} ${className}`}
      style={{ width: size, height: size, display: 'grid', placeItems: 'center' }}
    >
      {src ? (
        <img src={src} alt={name || ''} />
      ) : (
        <span
          style={{
            position: 'relative',
            zIndex: 1,
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: size * 0.42,
            letterSpacing: '0.05em',
            color: 'rgba(255,255,255,0.85)',
            textShadow: '0 1px 2px rgba(0,0,0,0.6)',
          }}
        >
          {initial}
        </span>
      )}
    </div>
  );
}

/**
 * Standard page header: monospace crumb + optional ref id, big display title,
 * and a row of action buttons.
 */
export function PageHead({ crumb, id, title, actions }) {
  return (
    <div className="page-head">
      <div className="titlebox">
        {(crumb || id) && (
          <div className="crumb">
            {crumb && <span>{crumb}</span>}
            {id && <><span style={{ color: 'var(--text-3)' }}>·</span><span className="id">{id}</span></>}
          </div>
        )}
        <h1>{title}</h1>
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

/** A single telemetry tile for the 4-up strip. */
export function Tile({ k, tone = '', children }) {
  return (
    <div className={`tile ${tone}`}>
      <div className="k">{k}</div>
      {children}
    </div>
  );
}
